import { useState, useEffect, useRef } from 'react';
import Hammer from 'hammerjs';

/**
 * Custom hook to handle swipe-to-delete and long-press logic.
 */
export const useSwipeToDelete = (
  itemId,
  { onDelete, onLongPress, onConfirmDelete, onHighlight },
  enableBulk,
  bulkDeletionMode,
  startBulkDeletion,
  translations
) => {
  const itemRef = useRef(null);
  const [deltaX, setDeltaX] = useState(0);
  const [isPanning, setIsPanning] = useState(false);
  const [swiped, setSwiped] = useState(false);
  const [showFallbackDeleteButton, setShowFallbackDeleteButton] = useState(false);
  const isSwipingRef = useRef(false);
  const initialTouchY = useRef(null);

  useEffect(() => {
    if (itemRef.current) {
      // Set touch-action to "pan-y" to explicitly allow vertical scrolling
      itemRef.current.style.touchAction = 'pan-y';
      let hammer;
      try {
        hammer = new Hammer(itemRef.current);
        
        // Configure Hammer to better differentiate between scrolling and swiping
        hammer.get('swipe').set({ 
          direction: Hammer.DIRECTION_HORIZONTAL, 
          threshold: 10,
          velocity: 0.3
        });
        
        // Only recognize horizontal pans, with a higher threshold
        hammer.get('pan').set({ 
          direction: Hammer.DIRECTION_HORIZONTAL, 
          threshold: 5,  // Lowered threshold for smoother start
          velocity: 0.2 
        });

        if (!bulkDeletionMode) {
          hammer.on('panstart', (e) => {
            // Only start panning if the movement is primarily horizontal
            if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
              setIsPanning(true);
              isSwipingRef.current = true;
              initialTouchY.current = e.center.y;
              // Prevent scrolling while swiping
              if (itemRef.current) {
                itemRef.current.style.touchAction = 'none';
              }
            }
          });

          hammer.on('panmove', (e) => {
            // If we're not swiping, ignore
            if (!isSwipingRef.current) return;
            
            // Check if user is trying to scroll (significant vertical movement)
            if (initialTouchY.current && Math.abs(e.center.y - initialTouchY.current) > 50) { // Increased threshold for smoother swiping
              // Cancel the swipe and allow scrolling
              setDeltaX(0);
              setIsPanning(false);
              isSwipingRef.current = false;
              if (itemRef.current) {
                itemRef.current.style.touchAction = 'pan-y';
              }
              return;
            }
            
            // Only process horizontal pans
            if (Math.abs(e.deltaY) > Math.abs(e.deltaX) * 1.5) {
              // Reset if vertical movement is dominant
              setDeltaX(0);
              setIsPanning(false);
              isSwipingRef.current = false;
              if (itemRef.current) {
                itemRef.current.style.touchAction = 'pan-y';
              }
              return;
            }
            
            // Only allow leftward (negative) movement
            if (e.deltaX < 0) {
              setDeltaX(e.deltaX);
            } else {
              setDeltaX(0);
            }
          });

          hammer.on('panend', async (e) => {
            // Reset touch action
            if (itemRef.current) {
              itemRef.current.style.touchAction = 'pan-y';
            }
            
            // If we weren't swiping, ignore
            if (!isSwipingRef.current) {
              setIsPanning(false);
              setDeltaX(0);
              return;
            }
            
            setIsPanning(false);
            isSwipingRef.current = false;
            initialTouchY.current = null;
            
            // If user swiped left far enough, confirm deletion
            if (e.deltaX < -80) { // Reduced threshold for easier triggering
              // Highlight the item before showing confirmation dialog
              if (onHighlight) {
                onHighlight(itemId);
              }
              
              // Use custom confirm function or fallback to default
              let shouldDelete = false;
              
              try {
                if (onConfirmDelete) {
                  shouldDelete = await onConfirmDelete();
                } else {
                  shouldDelete = window.confirm(translations.confirmDeletion || 'Are you sure you want to delete this item?');
                }
                
                if (shouldDelete) {
                  // Force reflow so that the CSS transition starts
                  if (itemRef.current) {
                    void itemRef.current.offsetWidth;
                  }
                  setSwiped(true);
                  // Animate off-screen by setting deltaX to full width (negative)
                  if (itemRef.current) {
                    setDeltaX(-itemRef.current.offsetWidth);
                  }
                  setTimeout(() => {
                    onDelete(itemId);
                  }, 300);
                } else {
                  // If user cancels, return to initial position
                  setDeltaX(0);
                  setSwiped(false);
                }
              } finally {
                // Always clear highlighting after confirmation dialog closes
                if (onHighlight) {
                  onHighlight(null);
                }
              }
            } else {
              // Otherwise, reset to original position and clear swiped flag
              setDeltaX(0);
              setSwiped(false);
            }
          });

          hammer.on('pancancel', () => {
            // Reset everything when pan is cancelled
            setDeltaX(0);
            setIsPanning(false);
            setSwiped(false);
            isSwipingRef.current = false;
            initialTouchY.current = null;
            if (itemRef.current) {
              itemRef.current.style.touchAction = 'pan-y';
            }
          });

          // Attach press event for bulk deletion if enabled and if more than one record exists
          if (enableBulk) {
            hammer.on('press', () => {
              // Only trigger bulk deletion if not already active and not swiping
              if (!bulkDeletionMode && !isSwipingRef.current) {
                startBulkDeletion(itemId);
                // Trigger haptic feedback vibration if supported
                if (typeof navigator !== 'undefined' && navigator.vibrate) {
                  navigator.vibrate(100); // 100ms vibration
                }
              }
            });
          }
        }
      } catch (err) {
        console.error('Hammer.js error:', err);
        setShowFallbackDeleteButton(true); // Show fallback delete button if an error occurs.
      }
      return () => {
        // Reset states on cleanup
        isSwipingRef.current = false;
        initialTouchY.current = null;
        if (itemRef.current) {
          itemRef.current.style.touchAction = 'pan-y';
        }
        if (hammer) {
          hammer.off('panstart');
          hammer.off('panmove');
          hammer.off('panend');
          hammer.off('pancancel');
          hammer.off('press');
          hammer.destroy();
        }
      };
    }
  }, [itemId, onDelete, enableBulk, bulkDeletionMode, startBulkDeletion, translations]);

  // Reset swipe state when bulk deletion mode changes
  useEffect(() => {
    if (bulkDeletionMode) {
      setDeltaX(0);
      setIsPanning(false);
      setSwiped(false);
      isSwipingRef.current = false;
    }
  }, [bulkDeletionMode]);

  return { itemRef, deltaX, isPanning, swiped, showFallbackDeleteButton, setDeltaX, setSwiped };
};
